import TEMPLATE_HTML from "./signatureTemplate.html";

/**
 * Unique HTML attribute written into every SignaFlow signature block.
 *
 * signatureAlreadyExists() searches the raw body HTML for this string
 * to decide whether a signature has already been inserted.  Using a
 * custom data-attribute is more reliable than text matching because
 * it survives Outlook's HTML normalisation and future copy-editing of
 * the visible signature text.
 */
export const SIGNATURE_MARKER_ATTR = "data-signaflow-signature";

export interface SignatureTemplateData {
  name: string;
  jobTitle: string;
  company: string;
  websiteUrl: string;
  phone: string;
}

/**
 * Returns the email-compatible HTML for a corporate signature.
 *
 * The template is stored in signatureTemplate.html and loaded here.
 * To customize the signature:
 *  1. Edit src/signature/signatureTemplate.html
 *  2. Use {{PLACEHOLDER}} syntax for data fields
 *  3. Save the file (webpack will bundle the updated template)
 *
 * Design rules:
 *  - Table-based layout only — Outlook strips CSS floats/flexbox/grid.
 *  - All CSS is inline — Outlook strips <style> blocks in received mail.
 *  - data-signaflow-signature="1" on the root <table> is the stable
 *    duplicate-detection marker used by signatureAlreadyExists().
 */
export function getSignatureHtml(data: SignatureTemplateData): string {
  // Process website URL display text
  const websiteDisplayText = data.websiteUrl
    .replace(/^https?:\/\//i, "")
    .replace(/\/$/, "");

  // Replace placeholders with actual data
  return TEMPLATE_HTML
    .replace(/{{NAME}}/g, escapeHtml(data.name))
    .replace(/{{JOB_TITLE}}/g, escapeHtml(data.jobTitle))
    .replace(/{{COMPANY}}/g, escapeHtml(data.company))
    .replace(/{{WEBSITE_URL}}/g, escapeHtml(data.websiteUrl))
    .replace(/{{WEBSITE_DISPLAY}}/g, escapeHtml(websiteDisplayText))
    .replace(/{{PHONE}}/g, escapeHtml(data.phone));
}

/**
 * Escapes HTML special characters to prevent injection or rendering issues.
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}
